import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { notificationAPI } from '../../services/api';

const TABS = [
  {
    key: 'home',
    label: 'Home',
    path: '/dashboard',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    key: 'search',
    label: 'Search',
    path: '/search',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
  },
  {
    key: 'progress',
    label: 'Progress',
    path: '/dashboard/progress',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
  },
  {
    key: 'notifications',
    label: 'Alerts',
    path: '/notifications',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
    hasBadge: true,
  },
  {
    key: 'profile',
    label: 'Profile',
    path: '/settings/email-preferences',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
];

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

export default function MobileBottomNav() {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const navigate = useNavigate();
  const location = useLocation();
  const [badgeCount, setBadgeCount] = useState(0);

  const fetchBadge = useCallback(() => {
    notificationAPI.unreadCount()
      .then(({ data }) => setBadgeCount(data.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    fetchBadge();
    const interval = setInterval(fetchBadge, 30000);
    return () => clearInterval(interval);
  }, [isMobile, fetchBadge]);

  if (!isMobile) return null;

  const activeTab = TABS.reduce((best, tab) => {
    if (location.pathname.startsWith(tab.path) && tab.path.length > best.path.length) {
      return tab;
    }
    return best;
  }, TABS[0]);

  return (
    <nav
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: 'var(--surface-primary, #ffffff)',
        borderTop: '1px solid var(--border-color, #e5e7eb)',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'stretch',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
      }}
      aria-label="Mobile navigation"
    >
      {TABS.map((tab) => {
        const isActive = activeTab.key === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => navigate(tab.path)}
            aria-label={tab.label}
            aria-current={isActive ? 'page' : undefined}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              minHeight: 56,
              minWidth: 44,
              padding: '6px 0 4px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: isActive
                ? 'var(--accent, #4f46e5)'
                : 'var(--text-secondary, #6b7280)',
              transition: 'color 0.15s ease',
              position: 'relative',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              {tab.icon}
              {tab.hasBadge && badgeCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: -4,
                    right: -6,
                    background: '#ef4444',
                    color: '#fff',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    borderRadius: '50%',
                    minWidth: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 3px',
                    lineHeight: 1,
                  }}
                >
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </span>
            <span
              style={{
                fontSize: '0.65rem',
                fontWeight: isActive ? 600 : 400,
                lineHeight: 1,
                letterSpacing: '0.01em',
              }}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
